/**
 * MessageBubble — renders a single chat message with WhatsApp-style formatting.
 */

import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, Pressable, TouchableOpacity } from 'react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import type { ChatMessage } from '../../types/api';
import { renderWhatsAppMarkdown } from '../../utils/whatsappMarkdown';
import { useTheme } from '../../utils/theme';
import InteractiveButtons from './InteractiveButtons';
import InteractiveList from './InteractiveList';
import { FONT_SIZE, FONT_FAMILY, SPACING, RADIUS, SHADOW } from '../../utils/design-tokens';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface Props {
  message: ChatMessage;
  onButtonPress: (buttonId: string, buttonLabel: string) => void;
  onListSelect: (itemId: string, itemLabel: string) => void;
  isSelected?: boolean;
  selectionMode?: boolean;
  onToggleSelection?: (msgId: string | number) => void;
  onRetry?: () => void;
  onReact?: (messageId: number, emoji: string) => void;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

/** WhatsApp-style delivery ticks for user messages */
function DeliveryTicks({ status }: { status?: string }) {
  if (!status) return null;
  if (status === 'failed') {
    return <Text style={styles.tickFailed}> ✗</Text>;
  }
  if (status === 'sent') {
    return <Text style={styles.tickSent}> ✓✓</Text>;
  }
  // 'sending' — single gray tick
  return <Text style={styles.tickSending}> ✓</Text>;
}

function MessageBubble({ message, onButtonPress, onListSelect, isSelected, selectionMode, onToggleSelection, onRetry, onReact, isFirstInGroup, isLastInGroup }: Props) {
  const isUser = message.direction === 'inbound';
  const { colors } = useTheme();
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  
  const meta = useMemo(
    () => (message.metadata ? JSON.parse(message.metadata) : null),
    [message.metadata],
  );

  const time = useMemo(
    () =>
      new Date(message.created_at_utc).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
    [message.created_at_utc],
  );

  const handleLongPress = () => {
    Haptics.selectionAsync();
    if (selectionMode && onToggleSelection) {
      onToggleSelection(message.id);
    } else if (onReact) {
      setShowReactionPicker(prev => !prev);
    } else if (onToggleSelection) {
      onToggleSelection(message.id);
    }
  };

  const handlePress = () => {
    if (showReactionPicker) {
      setShowReactionPicker(false);
    } else if (selectionMode && onToggleSelection) {
      Haptics.selectionAsync();
      onToggleSelection(message.id);
    } else if (message.status === 'failed' && onRetry) {
      onRetry();
    }
  };

  const handleReactionSelect = (emoji: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setShowReactionPicker(false);
    onReact?.(message.id, emoji);
  };

  const reactions = message.reactions || [];

  return (
    <Animated.View entering={isFirstInGroup ? FadeInDown.springify().damping(18).stiffness(120) : undefined}>
      <Pressable
        onLongPress={handleLongPress}
        onPress={handlePress}
        delayLongPress={300}
        style={[
          styles.row,
          isUser ? styles.rowUser : styles.rowBot,
          isSelected && styles.rowSelected,
          !isLastInGroup && styles.rowGrouped,
        ]}
      >
        <View style={[
          styles.bubble,
          isUser ? [styles.bubbleUser, { backgroundColor: colors.chatBubbleUser }] : [styles.bubbleBot, { backgroundColor: colors.chatBubbleBot, borderColor: colors.border }],
          isUser && !isFirstInGroup && { borderTopRightRadius: 12 },
          isUser && !isLastInGroup && { borderBottomRightRadius: 12 },
          !isUser && !isFirstInGroup && { borderTopLeftRadius: 12 },
          !isUser && !isLastInGroup && { borderBottomLeftRadius: 12 },
        ]}>
          {/* Message body */}
          {message.body ? (
            <Text style={[styles.text, isUser ? styles.textUser : { color: colors.chatBubbleBotText }]} selectable={true}>
              {renderWhatsAppMarkdown(message.body)}
            </Text>
          ) : null}

          {/* Interactive buttons */}
          {meta?.buttons && (
            <InteractiveButtons
              buttons={meta.buttons}
              footer={meta.footer}
              onPress={onButtonPress}
            />
          )}

          {/* Interactive list */}
          {meta?.sections && (
            <InteractiveList
              sections={meta.sections}
              buttonText={meta.buttonText}
              onSelect={onListSelect}
            />
          )}

          {/* Timestamp + delivery ticks — only on last message in group */}
          {isLastInGroup !== false && (
            <View style={styles.timeRow}>
              <Text style={[styles.time, isUser && styles.timeUser, !isUser && { color: colors.textDim }]}>{time}</Text>
              {isUser && <DeliveryTicks status={message.status} />}
            </View>
          )}
          {message.status === 'failed' && onRetry && (
            <Text style={styles.retryHint}>Tap to retry</Text>
          )}

          {/* Reaction display */}
          {reactions.length > 0 && (
            <View style={styles.reactionRow}>
              {reactions.map((r, i) => (
                <TouchableOpacity
                  key={`${r.emoji}-${i}`}
                  onPress={() => onReact?.(message.id, r.emoji)}
                  style={[styles.reactionBadge, { backgroundColor: colors.text + '0F' }]}
                >
                  <Text style={styles.reactionEmoji}>{r.emoji}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Reaction picker */}
        {showReactionPicker && (
          <View style={[styles.reactionPicker, { backgroundColor: colors.surface }, isUser ? styles.reactionPickerRight : styles.reactionPickerLeft]}>
            {REACTION_EMOJIS.map(emoji => (
              <TouchableOpacity key={emoji} onPress={() => handleReactionSelect(emoji)} style={styles.reactionPickerItem}>
                <Text style={styles.reactionPickerEmoji}>{emoji}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  row: {
    paddingHorizontal: SPACING.md,
    marginVertical: 2,
    paddingVertical: SPACING.xs,
  },
  rowGrouped: {
    marginVertical: 0,
    paddingVertical: 1,
  },
  rowSelected: {
    backgroundColor: 'rgba(44, 74, 98, 0.1)',
  },
  rowUser: {
    alignItems: 'flex-end',
  },
  rowBot: {
    alignItems: 'flex-start',
  },
  bubble: {
    maxWidth: '82%',
    borderRadius: RADIUS.xl,
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.md,
  },
  bubbleUser: {
    backgroundColor: '#2C4A62',
    borderTopRightRadius: RADIUS.xs,
  },
  bubbleBot: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: RADIUS.xs,
    borderWidth: 0.5,
    borderColor: 'rgba(139, 115, 85, 0.10)',
    ...SHADOW.subtle,
  },
  text: {
    fontSize: FONT_SIZE.base,
    fontFamily: FONT_FAMILY.body,
    color: '#1A202C',
    lineHeight: 21,
  },
  textUser: {
    color: '#fff',
  },
  time: {
    fontSize: FONT_SIZE.xs,
    color: '#94A3B8',
    marginTop: SPACING.xs,
  },
  timeUser: {
    color: 'rgba(255,255,255,0.7)',
  },
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginTop: 4,
  },
  tickSending: {
    fontSize: FONT_SIZE.xs,
    color: 'rgba(255,255,255,0.5)',
  },
  tickSent: {
    fontSize: FONT_SIZE.xs,
    color: '#34B7F1',
  },
  tickFailed: {
    fontSize: FONT_SIZE.xs,
    color: '#FF3B30',
  },
  retryHint: {
    fontSize: FONT_SIZE.xs,
    color: '#FF3B30',
    textAlign: 'right',
    marginTop: 2,
  },
  reactionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: SPACING.xs,
    gap: SPACING.xs,
  },
  reactionBadge: {
    backgroundColor: 'rgba(0,0,0,0.06)',
    borderRadius: RADIUS.md,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  reactionEmoji: {
    fontSize: FONT_SIZE.sm,
  },
  reactionPicker: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderRadius: RADIUS.xl,
    paddingHorizontal: SPACING.sm,
    paddingVertical: 6,
    marginTop: SPACING.xs,
    ...SHADOW.card,
  },
  reactionPickerRight: {
    alignSelf: 'flex-end',
  },
  reactionPickerLeft: {
    alignSelf: 'flex-start',
  },
  reactionPickerItem: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  reactionPickerEmoji: {
    fontSize: 22,
  },
});

export default React.memo(MessageBubble);
